/**
 * Clothing Set Editor Component
 * UI for creating and editing custom clothing sets based on weather conditions
 */

import { Cancel, CloudDownload, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { ClothingItem, ClothingSet, ClothingSetInput } from '@shared/types';
import React, { useState } from 'react';
import { getCurrentWeather, interpretWeatherCode } from '../../services/weatherService';

interface ClothingSetEditorProps {
  existingSet?: ClothingSet;
  onSave: (input: ClothingSetInput) => Promise<void>;
  onCancel: () => void;
}

const CLOTHING_OPTIONS: { value: ClothingItem; label: string; image: string }[] = [
  { value: 't-shirt-short', label: 'Short Sleeve', image: '/icons/cloths/tshirt short.png' },
  { value: 't-shirt-long', label: 'Long Sleeve', image: '/icons/cloths/tshirt long.png' },
  { value: 'sweater', label: 'Sweater', image: '/icons/cloths/sweater.png' },
  { value: 'shorts', label: 'Shorts', image: '/icons/cloths/shorts.png' },
  { value: 'long-tights', label: 'Long Tights', image: '/icons/cloths/thights.png' },
  { value: 'running-cap', label: 'Running Cap', image: '/icons/cloths/cap.png' },
  { value: 'sweatband', label: 'Sweatband', image: '/icons/cloths/sweatband.png' },
  { value: 'gloves', label: 'Gloves', image: '/icons/cloths/gloves.png' },
  { value: 'buff', label: 'Buff', image: '/icons/cloths/buff.png' },
];

const TOP_OPTIONS = CLOTHING_OPTIONS.filter(opt =>
  ['t-shirt-short', 't-shirt-long', 'sweater'].includes(opt.value)
);

const BOTTOM_OPTIONS = CLOTHING_OPTIONS.filter(opt =>
  ['shorts', 'long-tights'].includes(opt.value)
);

const ACCESSORY_OPTIONS = CLOTHING_OPTIONS.filter(opt =>
  ['running-cap', 'sweatband', 'gloves', 'buff'].includes(opt.value)
);

export const ClothingSetEditor: React.FC<ClothingSetEditorProps> = ({
  existingSet,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(existingSet?.name || '');
  const [tempRange, setTempRange] = useState<[number, number]>([
    existingSet?.temperature.min ?? 0,
    existingSet?.temperature.max ?? 20,
  ]);
  const [rain, setRain] = useState(existingSet?.conditions.rain ?? false);
  const [wind, setWind] = useState(existingSet?.conditions.wind ?? false);
  const [sun, setSun] = useState(existingSet?.conditions.sun ?? false);
  const [top, setTop] = useState<ClothingItem[]>(existingSet?.clothing.top || ['t-shirt-short']);
  const [bottom, setBottom] = useState<ClothingItem[]>(existingSet?.clothing.bottom || ['shorts']);
  const [accessories, setAccessories] = useState<ClothingItem[]>(
    existingSet?.clothing.accessories || []
  );
  const [saving, setSaving] = useState(false);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [weatherInfo, setWeatherInfo] = useState<string>('');

  const handleTempChange = (_event: Event, newValue: number | number[]) => {
    setTempRange(newValue as [number, number]);
  };

  const fetchCurrentWeather = async () => {
    setLoadingWeather(true);
    try {
      const weather = await getCurrentWeather();
      if (weather) {
        const weatherCondition = interpretWeatherCode(weather.weatherCode);

        // Set temperature range: current apparent temp (feels-like) ±2°C
        const minTemp = Math.round(weather.apparentTemperature - 2);
        const maxTemp = Math.round(weather.apparentTemperature + 2);
        setTempRange([minTemp, maxTemp]);

        // Set weather conditions based on current weather
        setRain(weatherCondition.isRaining);
        setWind(weather.windSpeed > 20); // Consider windy if > 20 km/h
        setSun(!weatherCondition.isRaining && weather.isDay);

        // Set weather info text
        const conditions = [];
        if (weatherCondition.isRaining) conditions.push('rainy');
        if (weather.windSpeed > 20) conditions.push(`windy (${weather.windSpeed} km/h)`);
        if (!weatherCondition.isRaining && weather.isDay) conditions.push('sunny');
        const conditionsText = conditions.length > 0 ? `, ${conditions.join(', ')}` : '';
        setWeatherInfo(
          `Fetched: ${weather.apparentTemperature}°C feels like (actual: ${weather.temperature}°C)${conditionsText} - ${weatherCondition.description}`
        );

        // Auto-generate name if empty
        if (!name.trim()) {
          const nameConditions = [];
          if (weatherCondition.isRaining) nameConditions.push('Rainy');
          if (weather.windSpeed > 20) nameConditions.push('Windy');
          if (!weatherCondition.isRaining && weather.isDay) nameConditions.push('Sunny');

          const tempDesc =
            weather.apparentTemperature < 10
              ? 'Cold'
              : weather.apparentTemperature > 20
                ? 'Warm'
                : 'Mild';
          const conditionDesc = nameConditions.length > 0 ? ` & ${nameConditions.join(' ')}` : '';
          setName(`${tempDesc}${conditionDesc} (${weather.apparentTemperature}°C)`);
        }
      }
    } catch (error) {
      console.error('Error fetching weather:', error);
      setWeatherInfo('');
      alert('Could not fetch current weather. Please try again.');
    } finally {
      setLoadingWeather(false);
    }
  };

  const toggleTop = (topItem: ClothingItem) => {
    setTop(prev => (prev.includes(topItem) ? prev.filter(t => t !== topItem) : [...prev, topItem]));
  };

  const toggleBottom = (bottomItem: ClothingItem) => {
    setBottom(prev =>
      prev.includes(bottomItem) ? prev.filter(b => b !== bottomItem) : [...prev, bottomItem]
    );
  };

  const toggleAccessory = (accessory: ClothingItem) => {
    setAccessories(prev =>
      prev.includes(accessory) ? prev.filter(a => a !== accessory) : [...prev, accessory]
    );
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      alert('Please enter a name for this clothing set');
      return;
    }

    if (tempRange[0] >= tempRange[1]) {
      alert('Minimum temperature must be less than maximum temperature');
      return;
    }

    setSaving(true);
    try {
      const input: ClothingSetInput = {
        name: name.trim(),
        temperature: { min: tempRange[0], max: tempRange[1] },
        conditions: { rain, wind, sun },
        clothing: {
          top: top.length > 0 ? top : ['t-shirt-short'],
          bottom: bottom.length > 0 ? bottom : ['shorts'],
          accessories,
        },
      };
      await onSave(input);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, overflow: 'auto', p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" gutterBottom>
          {existingSet ? 'Edit Clothing Set' : 'Create New Clothing Set'}
        </Typography>

        <Stack spacing={{ xs: 2, sm: 3 }} sx={{ mt: 2 }}>
          {/* Name and Fetch Weather Button */}
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <TextField
                label="Set Name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g., Cold & Rainy, Summer Run"
                fullWidth
                required
                aria-label="Clothing set name"
              />
              <Button
                variant="outlined"
                startIcon={<CloudDownload />}
                onClick={fetchCurrentWeather}
                disabled={loadingWeather}
                sx={{ minWidth: { xs: '100%', sm: 180 }, mt: 0 }}
                fullWidth={false}
                aria-label="Fetch current weather conditions"
              >
                {loadingWeather ? 'Loading...' : 'Fetch Weather'}
              </Button>
            </Stack>
            {weatherInfo && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {weatherInfo}
              </Typography>
            )}
          </Box>

          {/* Temperature Range */}
          <Box>
            <Typography gutterBottom>
              Temperature Range: {tempRange[0]}°C to {tempRange[1]}°C
            </Typography>
            <Slider
              value={tempRange}
              onChange={handleTempChange}
              valueLabelDisplay="auto"
              min={-20}
              max={40}
              marks={[
                { value: -20, label: '-20°C' },
                { value: 0, label: '0°C' },
                { value: 20, label: '20°C' },
                { value: 40, label: '40°C' },
              ]}
              aria-label="Temperature range for clothing set"
            />
          </Box>

          {/* Weather Conditions */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Weather Conditions
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <FormControlLabel
                control={
                  <Switch
                    checked={rain}
                    onChange={e => setRain(e.target.checked)}
                    aria-label="Toggle rain condition"
                  />
                }
                label="Rain"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={wind}
                    onChange={e => setWind(e.target.checked)}
                    aria-label="Toggle wind condition"
                  />
                }
                label="Wind"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={sun}
                    onChange={e => setSun(e.target.checked)}
                    aria-label="Toggle sun condition"
                  />
                }
                label="Sun"
              />
            </Stack>
          </Box>

          <Divider />

          {/* Top Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Top (select one or more)
            </Typography>
            <Grid container spacing={1}>
              {TOP_OPTIONS.map(option => (
                <Grid size={{ xs: 4 }} key={option.value}>
                  <Button
                    variant={top.includes(option.value) ? 'contained' : 'outlined'}
                    onClick={() => toggleTop(option.value)}
                    fullWidth
                    sx={{ height: '100%', flexDirection: 'column', py: 1.5 }}
                    aria-label={`Toggle ${option.label} as top clothing`}
                  >
                    <img src={option.image} alt={option.label} style={{ width: 40, height: 40 }} />
                    <Typography variant="caption" sx={{ mt: 1 }}>
                      {option.label}
                    </Typography>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Bottom Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Bottom (select one or more)
            </Typography>
            <Grid container spacing={1}>
              {BOTTOM_OPTIONS.map(option => (
                <Grid size={{ xs: 6 }} key={option.value}>
                  <Button
                    variant={bottom.includes(option.value) ? 'contained' : 'outlined'}
                    onClick={() => toggleBottom(option.value)}
                    fullWidth
                    sx={{ height: '100%', flexDirection: 'column', py: 1.5 }}
                    aria-label={`Toggle ${option.label} as bottom clothing`}
                  >
                    <img src={option.image} alt={option.label} style={{ width: 40, height: 40 }} />
                    <Typography variant="caption" sx={{ mt: 1 }}>
                      {option.label}
                    </Typography>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Accessories Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Accessories
            </Typography>
            <Grid container spacing={1}>
              {ACCESSORY_OPTIONS.map(option => (
                <Grid size={{ xs: 6, sm: 4 }} key={option.value}>
                  <Button
                    variant={accessories.includes(option.value) ? 'contained' : 'outlined'}
                    onClick={() => toggleAccessory(option.value)}
                    fullWidth
                    sx={{ height: '100%', flexDirection: 'column', py: 1.5 }}
                    aria-label={`Toggle ${option.label} as accessory`}
                  >
                    <img src={option.image} alt={option.label} style={{ width: 40, height: 40 }} />
                    <Typography variant="caption" sx={{ mt: 1 }}>
                      {option.label}
                    </Typography>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={onCancel}
              disabled={saving}
              aria-label="Cancel clothing set changes"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSubmit}
              disabled={saving}
              aria-label="Save clothing set"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
